// Brand line-art illustrations for the empty / error / not-found status
// screens (see status-screen.tsx). Deliberately inline SVG — same approach as
// icons.tsx — so they inherit the theme colours, add no network request, and
// render identically under SSR. Each sits on a soft coral disc (primary-50) so
// the three read as one family. `aria-hidden`: the StatusScreen heading carries
// the meaning, so the illustration is decorative to a screen reader.

export type StatusVariant = 'empty' | 'not-found' | 'error'

// `| undefined` is explicit so the optional className can be forwarded through
// StatusIllustration under the tsconfig's exactOptionalPropertyTypes.
type IllustrationProps = { className?: string | undefined }

// Shared soft disc behind every glyph — keeps the set visually coherent.
const Disc = () => <circle cx="80" cy="80" r="76" className="fill-primary-50" />

// No `role="img"` and `aria-hidden` on every <svg>: these carry no meaning of
// their own (the StatusScreen heading does), so they're purely decorative and
// hidden from assistive tech. `aria-hidden` is set as a literal attribute on
// each element (not spread from here) so the a11y lint can see it statically.
const commonSvgProps = {
  viewBox: '0 0 160 160',
  xmlns: 'http://www.w3.org/2000/svg',
}

// Empty search: a magnifying glass framing nothing — the short umber dash reads
// as "we looked here and found no one".
const EmptyIllustration = ({ className }: IllustrationProps) => (
  <svg {...commonSvgProps} aria-hidden="true" className={className}>
    <Disc />
    <circle cx="72" cy="72" r="30" className="fill-white stroke-primary-400" strokeWidth="7" />
    <line x1="60" y1="72" x2="84" y2="72" className="stroke-secondary-300" strokeWidth="6" strokeLinecap="round" />
    <line x1="94" y1="94" x2="118" y2="118" className="stroke-primary-400" strokeWidth="10" strokeLinecap="round" />
  </svg>
)

// Not found: a location pin — the app's core motif — with a question mark where
// the address should be. Signals "that place / provider isn't on the map".
const NotFoundIllustration = ({ className }: IllustrationProps) => (
  <svg {...commonSvgProps} aria-hidden="true" className={className}>
    <Disc />
    <path
      d="M80 32C60 32 45 47 45 67C45 92 80 118 80 118C80 118 115 92 115 67C115 47 100 32 80 32Z"
      className="fill-white stroke-primary-400"
      strokeWidth="7"
      strokeLinejoin="round"
    />
    <text
      x="80"
      y="80"
      textAnchor="middle"
      className="fill-secondary-600"
      style={{ font: '700 42px var(--font-display)' }}
    >
      ?
    </text>
  </svg>
)

// Error: a heartbeat trace that beats once and flatlines — a health-native way
// to say "our systems dropped the signal, not you".
const ErrorIllustration = ({ className }: IllustrationProps) => (
  <svg {...commonSvgProps} aria-hidden="true" className={className}>
    <Disc />
    <path
      d="M34 84H56L64 66L74 106L82 84H128"
      className="fill-none stroke-primary-400"
      strokeWidth="7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ILLUSTRATIONS: Record<StatusVariant, (props: IllustrationProps) => React.JSX.Element> = {
  empty: EmptyIllustration,
  'not-found': NotFoundIllustration,
  error: ErrorIllustration,
}

export const StatusIllustration = ({ variant, className }: { variant: StatusVariant } & IllustrationProps) => {
  const Illustration = ILLUSTRATIONS[variant]
  return <Illustration className={className} />
}
